/**
 * Torpedo views (plan G8): each live torpedo is drawn as a pooled `ord.torpedo` model instance with the
 * launching ship's faction look (the ordnance inherits its carrier's livery, contract §8) and its motor plume
 * lit (`setThrust(1, 0, false)`), nose along the velocity, at the interpolated position in render space
 * (D-011). Its weapon's trail effect (`torpedoTrail`) leaves the engine socket continuously at each emitter's
 * `count` particles per second, spread along the path flown since the last frame so the smoke stays
 * continuous at any frame rate.
 *
 * Views are pooled per faction and reused; building happens at init (`prewarm`) or when a pool runs dry.
 * Per-frame work is allocation-free (D-014).
 */
import { Quaternion, Vector3, type Object3D } from 'three/webgpu'
import type { LoadedModel, ModelInstance } from '../../assets/loader'
import type { TorpedoPool } from '../../combat/torpedoes'
import { MAX_FRAME_SECONDS } from '../../core/loop'
import type { Vec3 } from '../../core/math'
import type { ShipDef, WeaponsData } from '../../data/schemas'
import { FACTION_IDS, VFX_EFFECT_IDS, type FactionId, type VfxRecipeBook } from '../../render/contracts'
import { toRenderSpace } from '../../render/floatingOrigin'
import { applyShipLook, type MaterialLibrary, type ShipLook } from '../../render/materials'
import { MAX_ENTITIES, Ship } from '../../world/components'
import { emitSpec, type ParticleSystem } from './particles'

export interface TorpedoView {
  readonly faction: FactionId
  readonly instance: ModelInstance
  readonly look: ShipLook
}

export interface TorpedoViewsOptions {
  model: LoadedModel
  lib: MaterialLibrary
  /** Where live views are attached (the scene). */
  parent: Object3D
  lodScale: number
  /** Socket the trail leaves from (asset space); missing → the model origin. */
  engineSocket: string
  /** Torpedo pool capacity (one view slot per torpedo slot). */
  capacity: number
  recipes: VfxRecipeBook
}

export interface TorpedoViews {
  readonly parent: Object3D
  /** View per torpedo slot, null while the slot is free. */
  readonly slots: (TorpedoView | null)[]
  /** Pooled views per faction. */
  readonly free: Readonly<Record<FactionId, TorpedoView[]>>
  /** Views built so far. */
  created: number
  /** Live views. */
  live: number
  /** Engine socket position in asset space. */
  readonly engine: Vector3
  /** Per slot: ttl seen last frame (a larger ttl means the slot holds a new torpedo). */
  readonly lastTtl: Float32Array
  /** Per slot: float64 world engine position last frame (trail start). */
  readonly lastX: Float64Array
  readonly lastY: Float64Array
  readonly lastZ: Float64Array
  /** Per slot × trail emitter: fractional particles owed. */
  readonly trailAcc: Float32Array
  /** Trail emitters per slot in `trailAcc`. */
  readonly trailStride: number
  /** Sim time of the last update. */
  time: number
  build(faction: FactionId): TorpedoView
  prewarm(faction: FactionId, count: number): void
  dispose(): void
}

export function createTorpedoViews(opts: TorpedoViewsOptions): TorpedoViews {
  const free = {} as Record<FactionId, TorpedoView[]>
  for (const f of FACTION_IDS) free[f] = []
  const all: TorpedoView[] = []
  let stride = 1
  for (const id of VFX_EFFECT_IDS) stride = Math.max(stride, opts.recipes[id].length)
  const engine = new Vector3()
  const socket = opts.model.entry.sockets.find((s) => s.name === opts.engineSocket)
  if (socket) engine.set(socket.position[0], socket.position[1], socket.position[2])
  const views: TorpedoViews = {
    parent: opts.parent,
    slots: new Array<TorpedoView | null>(opts.capacity).fill(null),
    free,
    created: 0,
    live: 0,
    engine,
    lastTtl: new Float32Array(opts.capacity),
    lastX: new Float64Array(opts.capacity),
    lastY: new Float64Array(opts.capacity),
    lastZ: new Float64Array(opts.capacity),
    trailAcc: new Float32Array(opts.capacity * stride),
    trailStride: stride,
    time: 0,
    build(faction: FactionId): TorpedoView {
      const instance = opts.model.instantiate(opts.lodScale)
      instance.root.name = `torpedo:${faction}`
      const look = applyShipLook(opts.lib, instance, faction)
      look.setThrust(1, 0, false)
      const v: TorpedoView = { faction, instance, look }
      all.push(v)
      views.created++
      return v
    },
    prewarm(faction: FactionId, count: number): void {
      for (let i = 0; i < count; i++) free[faction].push(views.build(faction))
    },
    dispose(): void {
      for (let i = 0; i < views.slots.length; i++) {
        const v = views.slots[i]
        if (v) release(views, i, v)
      }
      for (let i = 0; i < all.length; i++) all[i].look.dispose()
      all.length = 0
      for (const f of FACTION_IDS) free[f].length = 0
    },
  }
  return views
}

function release(views: TorpedoViews, slot: number, v: TorpedoView): void {
  v.instance.root.removeFromParent()
  views.free[v.faction].push(v)
  views.slots[slot] = null
  views.live--
}

function acquire(views: TorpedoViews, slot: number, faction: FactionId): TorpedoView {
  const v = views.free[faction].pop() ?? views.build(faction)
  views.slots[slot] = v
  views.live++
  views.parent.add(v.instance.root)
  return v
}

/** The faction of the ship that launched a torpedo (its def's livery), `neutral` when unknown. */
export function launcherFaction(owner: number, defs: readonly ShipDef[]): FactionId {
  if (owner < 0 || owner >= MAX_ENTITIES) return 'neutral'
  const def = defs[Ship.defIndex[owner]]
  return def ? def.faction : 'neutral'
}

// Scratch (D-014).
const FORWARD = new Vector3(0, 0, -1)
const dirV = new Vector3()
const engineW = new Vector3()
const q = new Quaternion()

/**
 * Syncs views with the torpedo pool and draws this frame: pose at `alpha` between the previous and current
 * tick positions, render origin `cam` (float64), trails emitted up to sim time `now`.
 */
export function updateTorpedoViews(
  views: TorpedoViews,
  pool: TorpedoPool,
  weapons: WeaponsData,
  defs: readonly ShipDef[],
  sys: ParticleSystem,
  alpha: number,
  cam: Vec3,
  now: number,
): void {
  // Clamped like the loop's frame time, so a long gap never dumps a burst of smoke.
  const dt = now > views.time ? Math.min(now - views.time, MAX_FRAME_SECONDS) : 0
  views.time = now
  const n = pool.capacity < views.slots.length ? pool.capacity : views.slots.length
  const stride = views.trailStride
  for (let i = 0; i < n; i++) {
    let v = views.slots[i]
    if (pool.alive[i] !== 1) {
      if (v) release(views, i, v)
      continue
    }
    const faction = launcherFaction(pool.owner[i], defs)
    const ttl = pool.ttl[i]
    let fresh = false
    if (!v || v.faction !== faction || ttl > views.lastTtl[i] + 1e-4) {
      if (v) release(views, i, v)
      v = acquire(views, i, faction)
      fresh = true
    }
    views.lastTtl[i] = ttl

    // Pose: interpolated float64 position → render space; nose (-Z) along the velocity.
    const x = pool.px[i] + (pool.x[i] - pool.px[i]) * alpha
    const y = pool.py[i] + (pool.y[i] - pool.py[i]) * alpha
    const z = pool.pz[i] + (pool.z[i] - pool.pz[i]) * alpha
    const root = v.instance.root
    toRenderSpace(x, y, z, cam, root.position)
    const vx = pool.vx[i]
    const vy = pool.vy[i]
    const vz = pool.vz[i]
    const speed = Math.sqrt(vx * vx + vy * vy + vz * vz)
    if (speed > 1e-6) {
      dirV.set(vx / speed, vy / speed, vz / speed)
      root.quaternion.copy(q.setFromUnitVectors(FORWARD, dirV))
    }
    v.look.setThrust(1, 0, false)

    // Trail from the engine socket, along the path since last frame.
    engineW.copy(views.engine).applyQuaternion(root.quaternion)
    const ex = x + engineW.x
    const ey = y + engineW.y
    const ez = z + engineW.z
    const base = i * stride
    if (fresh) {
      views.lastX[i] = ex
      views.lastY[i] = ey
      views.lastZ[i] = ez
      for (let k = 0; k < stride; k++) views.trailAcc[base + k] = 0
    }
    const w = weapons.weapons[pool.weapon[i]]
    const trail = w ? w.vfx.trail : undefined
    if (trail !== undefined && dt > 0 && !fresh) {
      const specs = sys.recipes[trail]
      const sx = views.lastX[i]
      const sy = views.lastY[i]
      const sz = views.lastZ[i]
      const bx = speed > 1e-6 ? -vx / speed : 0
      const by = speed > 1e-6 ? -vy / speed : 0
      const bz = speed > 1e-6 ? -vz / speed : 1
      for (let e = 0; e < specs.length && e < stride; e++) {
        const spec = specs[e]
        let acc = views.trailAcc[base + e] + spec.count * dt
        const count = Math.floor(acc)
        acc -= count
        views.trailAcc[base + e] = acc
        for (let k = 0; k < count; k++) {
          const f = (k + 1) / count
          emitSpec(sys, spec, 1, sx + (ex - sx) * f, sy + (ey - sy) * f, sz + (ez - sz) * f, bx, by, bz, vx, vy, vz, now - (1 - f) * dt)
        }
      }
    }
    views.lastX[i] = ex
    views.lastY[i] = ey
    views.lastZ[i] = ez
  }
}
